import './BlockRcmAddToCartButton.css';
import t from 'rcm-i18n';
import React, { useState, useEffect } from 'react';
import type { CartSkuOption } from 'cart-core';
import {
  calcTranslatedSkuWarningMessages,
  calcCanAddSkuToCart,
} from 'cart-core';
import QuantitySelect from '../../cart-ui/src/component/QuantitySelect';
import { getDefaultSkuFromSkuArray } from 'cart-core';
import useVatCalculator from '../../block-all-products/src/useVatCalculator';
import InputTextField from '../../forms/src/fields/InputTextField';

interface Props {
  skuOptions: CartSkuOption[];
  onAddToCart: (items: { skuNumber: string; qty: number }[]) => void;
  onSkuSelected: (sku: CartSkuOption) => void;
  showPrice: boolean;
  showBvPv: boolean;
  showTextField: boolean;
}

interface State {
  quantity: number;
  selectedSkuIndex: number;
  selectedSku: CartSkuOption;
}

const BlockRcmAddToCartButton = ({
  skuOptions,
  onAddToCart,
  onSkuSelected,
  showPrice,
  showBvPv,
  showTextField,
}: Props) => {
  const [vatAmount, setVatAmount] = useState<undefined | number>();
  const [hideDropdown, setHideDropdown] = useState(false);
  const [amountIsValid, setAmountIsValid] = useState(true);
  const defaultSku = getDefaultSkuFromSkuArray(skuOptions);

  useEffect(() => {
    setVatAmount(window.pageProduct?.skus?.[0]?.data?.vatAmount);
  }, []);

  const [state, setState] = useState<State>({
    quantity: 1,
    selectedSkuIndex: skuOptions.findIndex(
      (sku: CartSkuOption) => sku.skuNumber === defaultSku.skuNumber
    ),
    selectedSku: defaultSku,
  });

  const selectedSku = state.selectedSku;
  const translatedWarningMessages = calcTranslatedSkuWarningMessages(
    selectedSku,
    state.quantity,
    amountIsValid
    );
  const _showVat = vatAmount !== undefined;
  const combinedPrice = useVatCalculator(
    selectedSku.formattedRetailPrice,
    vatAmount
  );
  const canAddToCart = calcCanAddSkuToCart(selectedSku, state.quantity, amountIsValid);
  const addToCart = () => {
    onAddToCart([
      {
        qty: state.quantity,
        skuNumber: state.selectedSku.skuNumber,
      },
    ]);
  };

  const handleQuantityChange = (event) => {
    if (event.type === 'input') {
      setHideDropdown(true);
      setAmountIsValid(String(event.target.value) === String(parseInt(event.target.value)));
    }
    if (event.target.value === '') {
      setHideDropdown(false);
      setAmountIsValid(true);
      setState((oldState) => ({
        ...oldState,
        quantity: 1,
      }));
    } else {
      setState((oldState) => ({
        ...oldState,
        quantity: parseInt(event.target.value),
      }));
    }
  };

  const handleSkuOptionChange = (event) => {
    const selectedSkuIndex = event.target.value;
    const _selectedSku = skuOptions[selectedSkuIndex];
    setState((oldState) => ({
      ...oldState,
      selectedSkuIndex,
      selectedSku: _selectedSku,
    }));
    onSkuSelected(selectedSku);
  };

  return (
    <div className='BlockRcmAddToCartButton'>
      <table>
        <tbody>
          {_showVat && (
            <tr>
              <td>
                <label>
                  <strong>{t('Price + VAT')}</strong>
                </label>
              </td>
              <td>
                <div className='retailPrice'>{combinedPrice}</div>
              </td>
            </tr>
          )}
          {showPrice && (
            <tr>
              <td>
                <label>
                  <strong>{t('Price')}</strong>
                </label>
              </td>
              <td>
                <div className='retailPrice'>
                  {selectedSku.formattedRetailPrice}
                </div>
              </td>
            </tr>
          )}
          {showBvPv && (
            <tr>
              <td>
                <label>{t('BV')}</label>
              </td>
              <td>
                <div className='bvPrice'>{selectedSku.bv}</div>
              </td>
            </tr>
          )}
          {showBvPv && (
            <tr>
              <td>
                <label>{t('PV')}</label>
              </td>
              <td>
                <div className='pvPrice'>{selectedSku.pv}</div>
              </td>
            </tr>
          )}
          {skuOptions.length > 1 && (
            <tr>
              <td>
                <label>{t('Option')}</label>
              </td>
              <td>
                <select
                  id='sku'
                  className='form-control'
                  value={state.selectedSkuIndex}
                  onChange={handleSkuOptionChange}
                >
                  {skuOptions.map((skuOption, i) => (
                    <option key={i} value={i}>
                      {skuOption.optionDescription}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          )}
          {!hideDropdown ? (
            <tr className='afterOptions'>
              <td>
                <label htmlFor='quantity'>{t('Quantity')}</label>
              </td>
              <td>
                <QuantitySelect
                  id='quantity'
                  className='form-control'
                  value={state.quantity}
                  onChange={handleQuantityChange}
                  isKalogris={skuOptions[0].skuNumber == 'KFCSH'}
                />
              </td>
            </tr>
          ) : null}

          {showTextField ? (
            <tr className='afterOptions'>
              <td>
                <label style={{ marginBottom: '12px' }} htmlFor="quantity">{t('Or enter custom amount')}</label>
              </td>
              <td>
                <div className="customForm">
                  <InputTextField className="donateField" onValueChange={() => handleQuantityChange(event)} />
                </div>
              </td>
            </tr>
          ) : null}

          {translatedWarningMessages.map((message, index) => (
            <tr key={index}>
              <td colSpan={2}>
                <div className='alert alert-warning'>{message}</div>
              </td>
            </tr>
          ))}
          {canAddToCart && (
            <tr>
              <td colSpan={2}>
                <a className="btn btn-primary BlockRcmAddToCartButton-addToCartButton" onClick={addToCart}>
                  {t('Add to Cart')}
                </a> 
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BlockRcmAddToCartButton;
